import { Injectable } from '@angular/core';
import { catchAndContinue, filterUndefined } from 'src/app/lego-utils';
import { BehaviorSubject, defer, Observable } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';
import { BomHeaderApiService } from 'src/app/services/bom-header-service';
import { handleError } from 'src/app/state/akita-error-helper';
import { IBomHeader } from 'src/app/state/bom-header/bom-header.model';
import { DesignService } from 'src/app/state/design.service';
import { ValueState } from 'src/app/state/value-state';

@Injectable({ providedIn: 'root' })
export class BOMHeaderService {
  private readonly state = new ValueState<IBomHeader | undefined>(undefined);
  private readonly hasLoadedSubject = new BehaviorSubject(false);

  public readonly bomHeader$ = this.state.value$;
  public readonly loading$ = this.state.loading$;
  public readonly error$ = this.state.error$;
  public readonly hasLoaded$ = this.hasLoadedSubject.asObservable();

  public readonly isBomLocked$ = this.state.selectValue((bomHeader) =>
    bomHeader ? Number(bomHeader.bomStatus) >= 22 : undefined
  );

  public readonly isBomCreated$ = this.bomHeader$.pipe(map((bomHeader) => !!bomHeader));

  public constructor(
    private readonly designService: DesignService,
    private readonly bomHeaderService: BomHeaderApiService
  ) {}

  public loadByDesign() {
    return this.designService.selectDesignId().pipe(
      filterUndefined(() => {
        this.state.reset();
        this.hasLoadedSubject.next(false);
      }),
      switchMap((designId) =>
        defer(() => {
          this.hasLoadedSubject.next(false);
          this.state.setLoading(true);

          return catchAndContinue(this.bomHeaderService.getSingle(designId), (error) => {
            this.state.set(undefined);
            handleError(this.state, error);
          }).pipe(
            tap({
              complete: () => {
                this.hasLoadedSubject.next(true);
              },
            }),
            finalize(() => {
              this.state.setLoading(false);
            })
          );
        })
      ),
      tap((bomHeader) => {
        this.state.set(bomHeader);
      }),
      map(() => undefined)
    );
  }

  public lockBOM(designId: string): Observable<void> {
    return defer(() => {
      this.state.setLoading(true);

      return this.bomHeaderService.lockBOM(designId).pipe(
        finalize(() => {
          this.state.setLoading(false);
        })
      );
    });
  }

  public createMaterialBom(designId: string): Observable<IBomHeader> {
    return defer(() => {
      this.state.setLoading(true);

      return this.bomHeaderService.createMaterialBom(designId).pipe(
        finalize(() => {
          this.state.setLoading(false);
        })
      );
    });
  }

  public resetStore() {
    this.state.reset();
    this.hasLoadedSubject.next(false);
  }
}
